import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import type { RootState } from '../../../shared/types';
import { useSearchAuditLogsMutation } from '../data-layer';
import type {
  AuditLogSearchRequest,
  AuditLogSearchResponse,
} from '../data-layer/auditLogTypes';
import {
  auditLogActions,
  type AuditLogState,
  selectAuditLogState,
} from '../data-store';
import type { TimelineData } from '../TimeLineBlock';
import isAbortError from '../utils/isAbortError';

import { useApiCancellation } from './useApiCancellation';

const DEFAULT_PAGE_SIZE = 50;

/**
 * Custom hook for managing audit log data
 * Provides methods to fetch audit logs and access current state
 */
interface UseAuditLogDataReturn {
  timelineData: TimelineData[];
  totalRecords: number;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  fetchAuditLogs: () => Promise<void>;
  searchAuditLogs: (searchText: string) => Promise<void>;
  refreshAuditLogs: () => void;
  clearAuditLogs: () => void;
  loadAuditLogsPage: (
    searchAfter: (string | number)[] | null,
    pageSize: number
  ) => Promise<AuditLogSearchResponse>;
}

const useAuditLogData = (): UseAuditLogDataReturn => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const auditLogState = useSelector(selectAuditLogState);
  // Keep a ref to always read the latest redux state in callbacks
  const auditLogStateRef = useRef(auditLogState);
  // Monotonic request id so stale/aborted requests can't flip loading off
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    auditLogStateRef.current = auditLogState;
  }, [auditLogState]);

  // RTK Query mutation hook
  const [searchAuditLogsMutation, { isLoading: isMutationLoading }] =
    useSearchAuditLogsMutation();

  // Use robust cancellation system
  const { registerAbort, cancelRequest } = useApiCancellation();

  // Validation helper for requestAccessKey
  const hasValidRequestAccessKey = useCallback(() => {
    const requestAccessKey =
      auditLogStateRef.current.requestMetadata?.requestAccessKey;

    return Boolean(requestAccessKey);
  }, []);

  // Build API payload
  const buildSearchPayload = useCallback(
    (
      searchText: string,
      searchAfter: (string | number)[] | null,
      pageSize: number
    ): AuditLogSearchRequest => {
      // Derive values from the latest redux state
      // Read directly from store to ensure we have the absolute latest filters
      // This is critical when filters are set from initialActiveFilters to avoid stale ref
      const currentState = selectAuditLogState(
        store.getState() as unknown as { aag: { auditLog: AuditLogState } }
      );
      const active = currentState.activeFilters;
      const allFilters = currentState.filters;

      // Allowed filter ids as provided by API -> UI (Columns API filters)
      const allowedIds = new Set(allFilters.map((f) => f.value));

      // Ensure carry-over/system filters are not dropped if server omits them in 'filters'
      allowedIds.add('auditLogStatuses');
      // Ensure entitlementDisplayNames is allowed when carried over from Transaction Overview
      allowedIds.add('entitlementDisplayNames');

      // Map UI filter ids (singular) to API payload keys (plural where required)
      const arrayKeyMap: Partial<
        Record<string, keyof AuditLogSearchRequest['filters']>
      > = {
        logTypes: 'logTypes',
        changeIndicators: 'changeIndicators',
        entitlementDisplayNames: 'entitlementDisplayNames',
        severities: 'severities',
        auditClasses: 'auditClasses',
        functionNames: 'functionNames',
        auditLogStatuses: 'auditLogStatuses',
        entitlementDeviation: 'entitlementDeviations',
      } as const;

      // Initialize with defaults
      const filters: AuditLogSearchRequest['filters'] = {
        searchText,
        logTypes: [],
        changeIndicators: [],
        entitlementDisplayNames: [],
        severities: [],
        auditClasses: [],
        entitlementDeviations: [],
        functionNames: [],
        auditLogStatuses: [],
        startDate: null,
        endDate: null,
        insightIds: [],
      };

      // entitlementDeviation handled via arrayKeyMap like other pills

      // Fill array filters only for ids allowed by API
      active.forEach((f) => {
        if (!allowedIds.has(f.value)) {
          return;
        }

        if (!(f.value in arrayKeyMap)) {
          return;
        }

        const payloadKey = arrayKeyMap[f.value];

        if (!payloadKey) {
          return;
        }

        const opts = Array.isArray(f.options) ? f.options : [];

        if (payloadKey === 'entitlementDeviations') {
          // Convert string labels to boolean values
          const booleanValues = opts
            .map((o) => (o as { label?: unknown }).label)
            .filter((v): v is string => typeof v === 'string' && v.length > 0)
            .map((label) => {
              const lowerLabel = label.toLowerCase();

              return lowerLabel === 'yes' || lowerLabel === 'true';
            });

          filters[payloadKey] = booleanValues;
        } else {
          const values = opts.map((o) => String(o.value));

          (filters[payloadKey] as string[]) = values;
        }
      });

      // Log Duration (date-time range) handling: convert selections to UTC ISO (Z)
      const logDuration = active.find((f) => f.value === 'logDuration');
      const opts =
        logDuration && Array.isArray(logDuration.options)
          ? logDuration.options
          : [];
      const startRaw = opts[0]?.value;
      const endRaw = opts[1]?.value;

      filters.startDate = startRaw
        ? new Date(String(startRaw)).toISOString()
        : null;
      filters.endDate = endRaw ? new Date(String(endRaw)).toISOString() : null;

      // Attach insightIds inside filters
      filters.insightIds = Array.isArray(currentState.insightIds)
        ? currentState.insightIds
        : [];

      const payload: AuditLogSearchRequest = {
        searchAfter,
        pageSize,
        requestAccessKey: currentState.requestMetadata?.requestAccessKey ?? '',
        filters,
      };

      return payload;
    },
    [store]
  );

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async () => {
    // Check for valid requestAccessKey before making API call
    if (!hasValidRequestAccessKey()) {
      return;
    }

    const requestId = latestRequestIdRef.current + 1;

    latestRequestIdRef.current = requestId;

    try {
      /* diagnostics removed */
      dispatch(auditLogActions.setLoading(true));
      dispatch(auditLogActions.setError(null));

      const payload = buildSearchPayload(
        auditLogStateRef.current.searchQuery,
        null,
        DEFAULT_PAGE_SIZE
      );

      // Cancel previous search request if any
      cancelRequest('searchAuditLogs');

      const promise = searchAuditLogsMutation(payload);

      // Register abort function for this request
      // RTK Query mutations have an abort() method
      const abortablePromise = registerAbort(
        promise as {
          abort?: () => void;
          unwrap: () => Promise<AuditLogSearchResponse>;
        },
        'searchAuditLogs'
      );

      const result: AuditLogSearchResponse = await abortablePromise.unwrap();

      // Store original data if no filters are active
      const hasActiveFilters =
        auditLogState.activeFilters.length > 0 ||
        auditLogState.searchQuery.trim().length > 0;

      if (
        !hasActiveFilters &&
        auditLogState.originalTimelineData.length === 0
      ) {
        dispatch(auditLogActions.setOriginalTimelineData(result.data));
      }

      // Persist entire search response
      dispatch(auditLogActions.setSearchResult(result));
      // Optionally store requestMetadata if needed by header/popover in redux in future
    } catch (error) {
      // Cancelling an in-flight request is expected (e.g. user changes filters quickly).
      // Do not surface aborted requests as errors, and do not wipe the UI state.
      if (isAbortError(error)) {
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch audit logs';

      dispatch(auditLogActions.setError(errorMessage));
      dispatch(
        auditLogActions.setTimelineData({
          data: [],
          totalRecords: 0,
        })
      );
    } finally {
      // Only the latest request should control the "loading" flag.
      if (latestRequestIdRef.current === requestId) {
        dispatch(auditLogActions.setLoading(false));
      }

      if (auditLogStateRef.current.suppressInitialFetch) {
        dispatch(auditLogActions.setSuppressInitialFetch(false));
      }
      /* diagnostics removed */
    }
  }, [
    dispatch,
    searchAuditLogsMutation,
    buildSearchPayload,
    hasValidRequestAccessKey,
    auditLogState.activeFilters.length,
    auditLogState.originalTimelineData.length,
    auditLogState.searchQuery,
    cancelRequest,
    registerAbort,
  ]);

  // Load a specific page (cursor-based). Appends when searchAfter is provided
  const loadAuditLogsPage = useCallback(
    async (searchAfter: (string | number)[] | null, pageSize: number) => {
      // Check for valid requestAccessKey before making API call
      if (!hasValidRequestAccessKey()) {
        throw new Error('Invalid request access key');
      }

      const payload = buildSearchPayload(
        auditLogStateRef.current.searchQuery,
        searchAfter,
        pageSize
      );

      // Cancel previous search request if any
      cancelRequest('searchAuditLogs');

      // Reflect loading state in Redux so other views (e.g., container) can react
      const requestId = latestRequestIdRef.current + 1;

      latestRequestIdRef.current = requestId;
      dispatch(auditLogActions.setLoading(true));
      const promise = searchAuditLogsMutation(payload);

      // Register abort function for this request
      // RTK Query mutations have an abort() method
      const abortablePromise = registerAbort(
        promise as {
          abort?: () => void;
          unwrap: () => Promise<AuditLogSearchResponse>;
        },
        'searchAuditLogs'
      );

      let result: AuditLogSearchResponse;

      try {
        result = await abortablePromise.unwrap();
      } catch (error) {
        // Ignore aborts triggered by newer searches/refreshes.
        if (isAbortError(error)) {
          return {
            data: Array.isArray(auditLogStateRef.current.timelineData)
              ? auditLogStateRef.current.timelineData
              : [],
            totalRecords:
              typeof auditLogStateRef.current.totalRecords === 'number'
                ? auditLogStateRef.current.totalRecords
                : 0,
            searchAfter: auditLogStateRef.current.searchAfter ?? null,
          };
        }

        throw error;
      } finally {
        // Ensure loading is reset for the pagination cycle
        if (latestRequestIdRef.current === requestId) {
          dispatch(auditLogActions.setLoading(false));
        }
      }

      if (searchAfter) {
        // Append to existing timeline data
        const current = Array.isArray(auditLogState.timelineData)
          ? auditLogState.timelineData
          : [];

        dispatch(
          auditLogActions.setTimelineData({
            data: [...current, ...result.data],
            totalRecords: result.totalRecords,
            searchAfter: result.searchAfter,
          })
        );
      } else {
        dispatch(auditLogActions.setSearchResult(result));
      }

      return result;
    },
    [
      searchAuditLogsMutation,
      buildSearchPayload,
      dispatch,
      hasValidRequestAccessKey,
      auditLogState.timelineData,
      cancelRequest,
      registerAbort,
    ]
  );

  // Initial data fetch on mount (with safe guards for undefined state)
  const timelineLength = Array.isArray(auditLogState.timelineData)
    ? auditLogState.timelineData.length
    : 0;
  const isLoadingState = Boolean(auditLogState.isLoading);
  const requestAccessKey =
    auditLogState.requestMetadata?.requestAccessKey ?? null;

  const didInitialFetchRef = useRef<string | number | null>(null);

  useEffect(() => {
    // Check for valid requestAccessKey first - if not available, wait for it
    if (!requestAccessKey || !hasValidRequestAccessKey()) {
      return;
    }

    // Skip if we've already fetched for this requestAccessKey
    if (didInitialFetchRef.current === requestAccessKey) {
      return;
    }

    if (timelineLength !== 0 || isLoadingState) {
      return;
    }

    // If insights-driven request was initiated (or back reset), skip the initial auto-fetch
    if (auditLogStateRef.current.suppressInitialFetch) {
      return;
    }

    // Cancel any pending search requests before making a new one
    cancelRequest('searchAuditLogs');

    didInitialFetchRef.current = requestAccessKey;

    /* diagnostics removed */
    fetchAuditLogs()
      .catch(() => {
        // Error handling is already done in the function
      })
      .finally(() => {
        /* diagnostics removed */
      });
  }, [
    fetchAuditLogs,
    timelineLength,
    isLoadingState,
    hasValidRequestAccessKey,
    requestAccessKey,
    cancelRequest,
  ]);

  // Search audit logs with query
  const searchAuditLogs = useCallback(
    async (searchText: string) => {
      // Check for valid requestAccessKey before making API call
      if (!hasValidRequestAccessKey()) {
        return;
      }

      const requestId = latestRequestIdRef.current + 1;

      latestRequestIdRef.current = requestId;

      try {
        dispatch(auditLogActions.setSearchQuery(searchText));
        dispatch(auditLogActions.setLoading(true));
        dispatch(auditLogActions.setError(null));

        const payload = buildSearchPayload(searchText, null, DEFAULT_PAGE_SIZE);

        // Cancel previous search request if any
        cancelRequest('searchAuditLogs');

        const promise = searchAuditLogsMutation(payload);

        // Register abort function for this request
        // RTK Query mutations have an abort() method
        const abortablePromise = registerAbort(
          promise as {
            abort?: () => void;
            unwrap: () => Promise<AuditLogSearchResponse>;
          },
          'searchAuditLogs'
        );

        const result: AuditLogSearchResponse = await abortablePromise.unwrap();

        dispatch(auditLogActions.setSearchResult(result));
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to search audit logs';

        dispatch(auditLogActions.setError(errorMessage));
        dispatch(
          auditLogActions.setTimelineData({
            data: [],
            totalRecords: 0,
          })
        );
      } finally {
        if (latestRequestIdRef.current === requestId) {
          dispatch(auditLogActions.setLoading(false));
        }
      }
    },
    [
      dispatch,
      searchAuditLogsMutation,
      buildSearchPayload,
      hasValidRequestAccessKey,
      cancelRequest,
      registerAbort,
    ]
  );

  // Refresh data
  const refreshAuditLogs = useCallback(() => {
    fetchAuditLogs()
      .catch(() => {
        // Error handling is already done in the function
      })
      .finally(() => undefined);
  }, [fetchAuditLogs]);

  // Clear data
  const clearAuditLogs = useCallback(() => {
    dispatch(auditLogActions.clearData());
  }, [dispatch]);

  return {
    // State
    timelineData: Array.isArray(auditLogState.timelineData)
      ? auditLogState.timelineData
      : [],
    totalRecords:
      typeof auditLogState.totalRecords === 'number'
        ? auditLogState.totalRecords
        : 0,
    isLoading: Boolean(auditLogState.isLoading) || isMutationLoading,
    error: auditLogState.error,
    searchQuery: auditLogState.searchQuery,

    // Actions
    fetchAuditLogs,
    searchAuditLogs,
    refreshAuditLogs,
    clearAuditLogs,
    loadAuditLogsPage,
  };
};

export default useAuditLogData;
